import { mapInstances } from "./constants.js";
import { featureInteriorPoint, geometryBounds, loadArizonaZipGeometry } from "./geo.js";
import { mapLabZipFeatureProps } from "./index.js";
import { zipPolygonPopupHtml } from "./popups.js";
import { copy, formatCopy } from "./runtime.js";

// ZIP search: zoom the front-door map to a typed ZIP and open the same infowindow a click
// produces. The front-door panel owns the search box, so this is scoped to that instance.
const FRONT_DOOR_MAP_ID = "frontDoorReportMap";

// One search popup at a time — a new search replaces the previous one.
let activeZipSearchPopup = null;

// Strip to digits and cap at 5 so partial/pasted input normalizes the same way the report
// ZIP fields do (see app.js input handler).
export function normalizeZipInput(rawInput) {
  return String(rawInput || "").replace(/\D/g, "").slice(0, 5);
}

export async function searchZipOnMap(rawInput) {
  const zipCode = normalizeZipInput(rawInput);
  if (zipCode.length !== 5) {
    return { ok: false, message: copy("map_zip_invalid") };
  }
  const map = mapInstances.get(FRONT_DOOR_MAP_ID);
  if (!map || !map.getContainer().isConnected) {
    return { ok: false, message: copy("map_still_loading") };
  }
  let zipGeometry;
  try {
    zipGeometry = await loadArizonaZipGeometry();
  } catch {
    return { ok: false, message: copy("map_boundaries_failed") };
  }
  const feature = (zipGeometry.features || []).find((candidate) => {
    const properties = candidate.properties || {};
    return (properties.GEOID || properties.ZCTA5) === zipCode;
  });
  if (!feature) {
    return { ok: false, message: formatCopy("map_zip_not_found", { zip: zipCode }) };
  }
  focusZipOnMap(map, feature, zipCode);
  return { ok: true, zipCode };
}

// geometryBounds returns [[west, south], [east, north]] — already Mapbox [sw, ne] order.
// fitBounds respects the map's maxBounds; maxZoom keeps small ZIPs from zooming in too far.
function focusZipOnMap(map, feature, zipCode) {
  map.fitBounds(geometryBounds(feature.geometry), {
    padding: 40,
    maxZoom: 10,
    duration: 600,
  });
  if (activeZipSearchPopup) {
    activeZipSearchPopup.remove();
    activeZipSearchPopup = null;
  }
  if (!window.mapboxgl) return;
  // Prefer the rendered (enriched) properties so counts match the active view; fall back to
  // the raw geometry props (popup then shows "No people reports yet") before the first build.
  const properties = mapLabZipFeatureProps(zipCode) || feature.properties || {};
  activeZipSearchPopup = new window.mapboxgl.Popup({ closeButton: true, maxWidth: "260px" })
    .setLngLat(featureInteriorPoint(feature))
    .setHTML(zipPolygonPopupHtml(properties))
    .addTo(map);
}
